import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Heart, BookOpen, GraduationCap, Home } from "lucide-react";
import Panda from "../components/Panda";
import { CurveBottom, Bamboo } from "../components/Decor";
import { author } from "../data/parents";
import { BOOK_URL, modules } from "../data/mock";

const About = () => (
  <div data-testid="about-page">
    <section className="relative bg-[#f7931e] overflow-hidden">
      <Bamboo className="absolute -right-6 -top-8 hidden md:block opacity-80" height={380} flip />
      <div className="relative max-w-5xl mx-auto px-6 pt-12 pb-24 flex flex-col md:flex-row items-center gap-10">
        <a href={BOOK_URL} target="_blank" rel="noreferrer" className="book-cover" aria-label="Get the book on Amazon">
          <div className="book-spine" />
          <div className="book-front">
            <p className="text-[10px] tracking-[0.3em] uppercase text-white/80">An age-based family curriculum</p>
            <h3 className="text-white font-bold text-2xl leading-tight mt-2" style={{ fontFamily: "Literata, serif" }}>Teach Your Child Credit</h3>
            <div className="my-3 flex justify-center"><Panda size={64} accessory="cap" scarf="#f3c932" item="coin" /></div>
            <p className="text-white/90 text-xs">Building Lifelong Financial Confidence</p>
            <p className="text-white font-bold text-sm mt-3">Ken Bossard, MBA</p>
          </div>
        </a>
        <div className="text-center md:text-left">
          <p className="text-[#8a4b00] font-bold uppercase tracking-widest text-sm">About the book</p>
          <h1 className="text-white font-bold text-4xl sm:text-5xl mt-2 leading-tight drop-shadow-[0_2px_0_rgba(0,0,0,0.2)]" style={{ fontFamily: "Literata, serif" }}>Teach Your Child Credit</h1>
          <p className="text-[#5a3000] text-lg mt-3 font-medium">An Age-Based Family Curriculum for Building Lifelong Financial Confidence</p>
          <p className="text-[#8a4b00] mt-1">For parents, grandparents, guardians, educators, and mentors of children and young adults.</p>
          <a href={BOOK_URL} target="_blank" rel="noreferrer" className="mt-6 btn-pill-lg bg-[#e5356f] hover:bg-[#c92a5e] text-white shadow-xl inline-flex items-center gap-2" data-testid="about-get-book">Get the book on Amazon <ExternalLink size={18} /></a>
        </div>
      </div>
      <CurveBottom color="#ffffff" />
    </section>

    <section className="max-w-5xl mx-auto px-6 py-16 space-y-12">
      <div className="grid md:grid-cols-3 gap-6">
        {[
          [BookOpen, "Five age-based modules", "Roughly ages 5-8, 9-11, 12-14, 15-17 and 18-24 - open to the chapter that matches your child."],
          [Home, "Built for the kitchen table", "Each module has learning objectives, a parent conversation script, a reproducible worksheet and a short case study."],
          [GraduationCap, "Fact-checked for 2026", "Utilization targets, the FCRA seven-year clock, FICO history and mortgage scoring models were all corrected and documented."],
        ].map(([Icon, t, d]) => (
          <div key={t} className="bg-white rounded-3xl shadow-md p-7 border-t-8 border-[#63c79a] hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow]">
            <span className="w-12 h-12 rounded-2xl bg-[#e8f8ef] text-[#2e8b57] flex items-center justify-center"><Icon /></span>
            <h3 className="font-bold text-[#0c4a6e] text-xl mt-4" style={{ fontFamily: "Literata, serif" }}>{t}</h3>
            <p className="text-gray-600 mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>

      <div id="author" className="bg-[#e9f9fd] rounded-3xl p-8 sm:p-10 flex flex-col md:flex-row gap-8 items-center">
        <div className="w-40 h-40 rounded-full bg-white shadow-inner flex items-center justify-center flex-shrink-0 overflow-hidden">
          <Panda size={120} accessory="glasses" scarf="#1b8bc9" />
        </div>
        <div>
          <p className="text-[#1b8bc9] font-bold uppercase tracking-widest text-sm">About the author</p>
          <h2 className="text-3xl font-bold text-[#0c4a6e] mt-1" style={{ fontFamily: "Literata, serif" }}>{author.name}</h2>
          <p className="text-gray-700 mt-3 leading-relaxed text-lg">{author.bio}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white rounded-3xl shadow-md p-8">
          <h3 className="text-2xl font-bold text-[#0c4a6e] mb-3" style={{ fontFamily: "Literata, serif" }}>Editor's note</h3>
          <p className="text-gray-700 leading-relaxed">{author.editorsNote}</p>
          <Link to="/parents/facts" className="inline-block mt-4 font-bold text-[#1b8bc9] hover:text-[#e5356f]">See every correction</Link>
        </div>
        <div className="bg-[#fff8e1] rounded-3xl p-8 flex flex-col justify-center">
          <Heart className="text-[#e5356f]" fill="#e5356f" />
          <h3 className="text-2xl font-bold text-[#8a6d00] mt-3" style={{ fontFamily: "Literata, serif" }}>Dedication</h3>
          <p className="text-gray-700 mt-2 leading-relaxed italic">"{author.dedication}"</p>
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-[#0c4a6e] mb-6 text-center" style={{ fontFamily: "Literata, serif" }}>Inside the curriculum</h3>
        <div className="flex flex-wrap justify-center gap-3">
          {modules.map((m) => (
            <Link key={m.id} to={`/lessons/${m.slug}`} className="btn-pill text-white shadow hover:-translate-y-0.5 transition-transform" style={{ background: m.button }}>
              {m.num}. {m.short} <span className="opacity-80 font-normal">({m.ages})</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default About;
